import { DatabaseModel, DatabaseModelElement, GetResponse } from "./DatabaseModel";
import { TableCell, TableRow } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import React from "react";
// @ts-ignore
import { Link } from "react-router";
import Debtor from "./Debtor";
import Reseller from "./Reseller";
import { formatDate } from "../utils/formatDate";
import { isAfter, isBefore } from "date-fns";
import Venue from "./Venue";
import PricePlan from "./PricePlan";
import axios, { AxiosResponse } from "axios";
import SubscriptionLine from "./SubscriptionLine";

export enum Status {
  ACTIVE = "ACTIVE",
  BLOCKED = "BLOCKED",
  UNBLOCKED = "UNBLOCKED",
}

export enum StatusInvoicing {
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
}

export enum Period {
  MONTH = "MONTH",
  YEAR = "YEAR",
}

export default class Subscription extends DatabaseModel implements DatabaseModelElement {
  static modelName = "subsSubscription";

  constructor(
    public id: number = 0,
    public venue_id: number = 0,
    public subs_debtor_id: number | null = null,
    public subs_reseller_id: number | null = null,
    public subs_price_plan_id: number = 0,
    public start_date: Date | null = null,
    public end_date: Date | null = null,
    public start_invoicing_date: Date | null = null,
    public next_date: Date | null = null,
    public payment_period_in_days: number = 14,
    public block_period_in_days: number = 14,
    public status: Status = Status.ACTIVE,
    public status_invoicing: StatusInvoicing = StatusInvoicing.ACTIVE,
    public period: Period = Period.MONTH,
    public invoice_period: object = { every: { months: 1 } },
    public invoice_type: string | null = null,
    public months_left: number = 0,
    public created_at: Date = new Date(),
    public updated_at: Date = new Date(),
    public deleted_at: Date | null = null,
    public venue: Venue | null = null,
    public debtor: Debtor | null = null,
    public reseller: Reseller | null = null,
    public pricePlan: PricePlan | null = null,
    public subscriptionLines: Array<SubscriptionLine> | null = null
  ) {
    super();
  }

  static CreateModel(model: any): Subscription {
    return new Subscription(
      model.id,
      model.venue_id,
      model.subs_debtor_id,
      model.subs_reseller_id,
      model.subs_price_plan_id,
      model.start_date ? new Date(model.start_date) : null,
      model.end_date ? new Date(model.end_date) : null,
      model.start_invoicing_date ? new Date(model.start_invoicing_date) : null,
      model.next_date ? new Date(model.next_date) : null,
      model.payment_period_in_days,
      model.block_period_in_days,
      model.status,
      model.status_invoicing,
      model.period,
      model.invoice_period,
      model.invoice_type,
      model.months_left,
      new Date(model.created_at),
      new Date(model.updated_at),
      model.deleted_at ? new Date(model.deleted_at) : null,
      model.venue ? Venue.CreateModel(model.venue) : null,
      model.subs_debtor ? Debtor.CreateModel(model.subs_debtor) : null,
      model.subs_reseller ? Reseller.CreateModel(model.subs_reseller) : null,
      model.subs_price_plan ? PricePlan.CreateModel(model.subs_price_plan) : null,
      model.subs_subscription_lines
        ? model.subs_subscription_lines.map((subscriptionLine: any) => SubscriptionLine.CreateModel(subscriptionLine))
        : null
    );
  }

  isActive(): boolean {
    const statusOk = this.status === Status.ACTIVE || this.status === Status.UNBLOCKED;
    const timePeriodOk =
      this.start_date != null &&
      (this.end_date == null || isBefore(new Date(), this.end_date)) &&
      isAfter(new Date(), this.start_date);

    return statusOk && timePeriodOk;
  }

  static makeInvoice(id: number) {
    return new Promise((resolve: (value: any) => void, reject) => {
      axios
        .post("core/" + Subscription.modelName + "/invoice", { id })
        .then((response: AxiosResponse) => {
          resolve({ response });
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public apiEndSubscription(endDate: any, sendFinalInvoice: boolean, finalInvoiceDate: any): Promise<any> {
    // only send newValues or all values if there are no newValues set
    const data: any = {};
    data.end_date = endDate;
    data.send_final_invoice = sendFinalInvoice;
    data.final_invoice_date = finalInvoiceDate;
    data.id = this.id;
    return new Promise((resolve: (value: GetResponse<any>) => void, reject) => {
      axios
        .post("core/" + (this.constructor as typeof DatabaseModel).modelName + "/endSubscription", data)
        .then((response) => {
          resolve({
            model: (this.constructor as typeof DatabaseModel).CreateModel(response.data.data),
            response,
          });
        });
    });
  }

  makeInvoice() {
    return Subscription.makeInvoice(this.id);
  }

  static apiReset(id: number) {
    return new Promise((resolve: (value: any) => void, reject) => {
      axios
        .post("core/" + Subscription.modelName + "/reset", { id })
        .then((response: AxiosResponse) => {
          resolve({ response });
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  apiReset() {
    return Subscription.apiReset(this.id);
  }

  static tableRowHeader() {
    return (
      <TableRow>
        <TableCell>Start Date</TableCell>
        <TableCell>Debtor</TableCell>
        <TableCell>Reseller</TableCell>
      </TableRow>
    );
  }

  tableRow() {
    return (
      <TableRow>
        <TableCell>{this.start_date != null ? formatDate(this.start_date) : "Not started"}</TableCell>
        <TableCell>
          {this.subs_debtor_id == null ? (
            "No debtor assigned"
          ) : (
            <Link to={{ pathname: Debtor.editPath(this.subs_debtor_id) }}>{this.debtor?.company_name ?? "Debtor"}</Link>
          )}
        </TableCell>
        <TableCell>
          {this.subs_reseller_id == null ? (
            "No reseller assigned"
          ) : (
            <Link to={{ pathname: Reseller.editPath(this.subs_reseller_id) }}>
              {this.reseller?.description ?? "Reseller"}
            </Link>
          )}
        </TableCell>
        <TableCell>
          <Link to={{ pathname: Subscription.editPath(this.id) }}>
            <Visibility />
          </Link>
        </TableCell>
      </TableRow>
    );
  }
}
